<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { setLocale, SUPPORTED } from '@/i18n'
import type { Locale } from '@/i18n'

const { locale, t } = useI18n()

const activeLocale = computed(() => locale.value as Locale)

// Mobile compact dropdown state. The segmented control at `sm:` and above
// needs none of this — it is a static set of buttons.
const rootEl = ref<HTMLElement | null>(null)
const triggerEl = ref<HTMLButtonElement | null>(null)

// Escape returns focus to the trigger. An outside click deliberately does not: the browser
// is already moving focus to whatever the user clicked (or, if they clicked a non-focusable
// area, leaving it on <body> as expected). Forcing it back would fight the user's own action.
const dropdown = useDismissablePopover({
  anchorRef: triggerEl,
  boundaryRef: rootEl,
  singleOpen: true,
  closeOnEscape: true,
  returnFocusOnEscape: true,
  closeOnOutside: ['click'],
})
const open = dropdown.isOpen

function select(lang: Locale): void {
  setLocale(lang)
  // Selection is a deliberate keyboard/click action that removes the panel
  // (and the option the user just activated) from the DOM — return focus to
  // the trigger so keyboard users don't lose their place in the page.
  dropdown.close({ returnFocus: true })
}

function toggleDropdown(): void {
  // Focus is already on the trigger (the user just activated it), so
  // there is nothing to restore when closing.
  dropdown.toggle()
}
</script>

<template>
  <div ref="rootEl" class="relative inline-block text-left">
    <!-- Segmented control: unchanged presentation, sm: and above -->
    <div
      class="hidden items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 sm:inline-flex"
      role="group"
      :aria-label="t('language_toggle.label')"
    >
      <button
        v-for="lang in SUPPORTED"
        :key="lang"
        type="button"
        :aria-pressed="activeLocale === lang"
        :aria-label="t(`languages.${lang}`)"
        class="rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-emerald-500"
        :class="
          activeLocale === lang
            ? 'bg-emerald-500 text-white'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
        "
        @click="select(lang)"
      >
        {{ lang }}
      </button>
    </div>

    <!-- Compact dropdown: below sm, so it fits a narrow column without adding a row -->
    <div class="sm:hidden">
      <button
        ref="triggerEl"
        type="button"
        class="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-emerald-500"
        :aria-expanded="open"
        :aria-label="t('language_toggle.label')"
        @click="toggleDropdown"
      >
        {{ activeLocale }}
        <svg class="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Plain disclosure panel: an ordinary list of buttons in native tab
           order, not an ARIA menu. See FINDING-003 in issue #176 for why. -->
      <div
        v-if="open"
        class="absolute right-0 z-50 mt-1 w-28 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
      >
        <button
          v-for="lang in SUPPORTED"
          :key="lang"
          type="button"
          class="flex w-full items-center px-3 py-1.5 text-left text-xs font-medium transition-colors"
          :class="
            activeLocale === lang
              ? 'bg-emerald-50 text-emerald-700'
              : 'text-slate-600 hover:bg-slate-50'
          "
          @click="select(lang)"
        >
          {{ t(`languages.${lang}`) }}
        </button>
      </div>
    </div>
  </div>
</template>
